import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import { Button, Table, Text, View } from 'src/components/common';
import './styles.scss';
import { IRootState } from 'src/redux/rootReducer';
import { GetTimesheetsParams } from 'src/redux/timesheet/types';
import { MUIDataTableOptions } from 'mui-datatables';
import {
  exportCSVTimesheetsAsync,
  getTimesheetsAsync,
  setActiveTimesheetTab,
  setDateRange,
  setSelectedTimesheet,
} from 'src/redux/timesheet/actions';
import { filterTimesheet, getColumns, getOptions, TabIdx, TimesheetTabs } from './tableHelpers';
import { Navigator } from 'src/services';
import { PATHS } from 'src/appConfig/paths';
import DateRangePicker from './DateRangePicker';
import { InitialWeeklyTableData } from 'src/redux/timesheet/reducer';
import { DateFormat, getInitialDateRange } from 'src/utils/momentUtils';
import { getWeekDays, TIMESHEETS_TABLE_TAKE } from 'src/containers/TimesheetContainers/helpers';
import _ from 'lodash';

// Foreman: Open Timesheet -> sort by Employee Last Name :))
// Admin: Weekly Timesheet -> sort by Trade, Employee ID
const Timesheet: React.FC<Props> = ({
  timesheetDate,
  trades,
  levels,
  user,
  onGetTimesheets,
  onSetSelectedTimeSheet,
  onExportCSVTimesheets,
  onSetActiveTab,
  onSetDateRange,
  // onSetQueryParams,
}) => {
  const {
    timesheets,
    totalTimesheet,
    isLoading: loading,
    activeTimesheetTab: activeTab,
    dateRange: dates,
    // queryParams: params,
  } = timesheetDate;

  const isShowAllTimesheet = user?.role === 'ADMIN';
  const DEFAULT_ALL_SORT = 'trade';
  const DEFAULT_WEEK_SORT = isShowAllTimesheet ? 'trade' : 'employeeLastName';
  const [params, setParams] = useState<GetTimesheetsParams>();

  useEffect(() => {
    onGetTimesheets(filterTimesheet(getUpdatedParams()));
    localStorage.removeItem(TIMESHEETS_TABLE_TAKE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates]);

  const getUpdatedParams = (inputParams?: GetTimesheetsParams) => {
    const updatedParams = { ...(inputParams ?? params) };

    return {
      ...updatedParams,
      skip: updatedParams.skip ?? 0,
      take: Number(localStorage[TIMESHEETS_TABLE_TAKE])
        ? Number(localStorage[TIMESHEETS_TABLE_TAKE])
        : updatedParams.take
        ? updatedParams.take
        : 10,
      sort: updatedParams.sort ?? (activeTab === TabIdx.All ? DEFAULT_ALL_SORT : DEFAULT_WEEK_SORT),
      order: updatedParams.order ?? 'asc',
      startDate: moment(dates[0]).format('YYYY-MM-DD'),
      endDate: moment(dates[1]).format('YYYY-MM-DD'),
    };
  };

  const handleGetTimesheets = (params: GetTimesheetsParams) => {
    const updatedParams = getUpdatedParams(params);
    setParams(updatedParams);
    onGetTimesheets(filterTimesheet(updatedParams));
  };

  const navigateToDetailPage = (id: string) => {
    localStorage.setItem(TIMESHEETS_TABLE_TAKE, JSON.stringify(params?.take));
    Navigator.navigate(`${PATHS.updateTimesheet}/${id}`);
  };

  const handleChangeTab = tabIndex => {
    onSetDateRange(getInitialDateRange(tabIndex === TabIdx.All));
    setParams({
      ...params,
      take: Number(localStorage[TIMESHEETS_TABLE_TAKE]) ? Number(localStorage[TIMESHEETS_TABLE_TAKE]) : params.take,
      sort: tabIndex === TabIdx.All ? DEFAULT_ALL_SORT : DEFAULT_WEEK_SORT,
      direction: 'asc',
    });
    onSetActiveTab(tabIndex);
  };

  const handleDateRangeChange = dates => {
    // activeTab === 'All' if 1st day of month is not 1st date of week => get 1st date of week
    if (activeTab === TabIdx.All) {
      const startDate = moment(dates[0]).startOf('week').toDate();
      const endDate = moment(dates[1]).endOf('week').toDate();
      onSetDateRange([startDate, endDate]);
    } else {
      onSetDateRange(dates);
    }
  };

  const handleDownloadCSV = data => {
    const updatedParams = getUpdatedParams();
    onExportCSVTimesheets(filterTimesheet({ ...updatedParams, takeAll: true, sort: undefined }));
  };

  const navigateToRequestNewPage = () => {
    const startDate = moment(dates[0]).format(DateFormat);
    const endDate = moment(dates[1]).format(DateFormat);
    onSetSelectedTimeSheet({
      ...InitialWeeklyTableData,
      startDate: startDate,
      endDate: endDate,
      dailyTimes: getWeekDays(startDate, endDate),
    });

    Navigator.navigate(PATHS.createTimesheet);
  };
  var sortedTimesheet = [...timesheets];

  if (params?.sort === 'trade' && isShowAllTimesheet) {
    sortedTimesheet = _.orderBy(
      sortedTimesheet,
      ['trade', 'employeeId'],
      [params.order as boolean | 'asc' | 'desc', 'asc'],
    );
  }

  const handleRowClick = (_value: any, meta: { rowIndex: number }) => {
    const index = meta.rowIndex;
    const selectedRecord = sortedTimesheet[index];
    //Navigate to Detail Page
    navigateToDetailPage(selectedRecord.id);
  };

  const tableOptions: MUIDataTableOptions = getOptions(
    activeTab === TabIdx.All || activeTab === TabIdx.Week,
    totalTimesheet,
    handleRowClick,
    handleDownloadCSV,
    Number(localStorage[TIMESHEETS_TABLE_TAKE]) ? Number(localStorage[TIMESHEETS_TABLE_TAKE]) : params?.take,
  );

  return (
    <View className="ctn-timesheet mt-32">
      <View isRow justify="space-between">
        <h3>{isShowAllTimesheet ? 'Manage Timesheets' : 'Open Timesheets'}</h3>
        {activeTab === TabIdx.Week && (
          <Button label="ADD NEW TIMESHEET" style={{ width: 'fit-content' }} onClick={navigateToRequestNewPage} />
        )}
      </View>
      <View className="mt-24">
        <View isRow justify={isShowAllTimesheet ? 'space-between' : 'flex-end'} align="center" className="mb-16">
          {isShowAllTimesheet && (
            <span className={'ctn-second-nav-option'}>
              {TimesheetTabs.map(tab => (
                <Text
                  key={tab.label}
                  className={cn('ctn-second-nav-item', {
                    focus: activeTab === tab.index,
                  })}
                  onClick={() => handleChangeTab(tab.index)}>
                  {tab.label}
                </Text>
              ))}
            </span>
          )}
          <View isRow align="center">
            <DateRangePicker
              selecteds={dates}
              onChange={handleDateRangeChange}
              jumpRange={activeTab === TabIdx.All ? 'month' : 'week'}
              rangeSelectable={activeTab === TabIdx.All}
              allowToJump={isShowAllTimesheet}
            />
          </View>
        </View>

        <Table
          key={`table-timesheet-${activeTab}`}
          title=""
          data={sortedTimesheet}
          isLoading={loading}
          tableOptions={tableOptions}
          defaultSortOrder={{
            name: activeTab === TabIdx.All ? DEFAULT_ALL_SORT : DEFAULT_WEEK_SORT,
            direction: 'asc',
          }}
          columns={getColumns(activeTab === TabIdx.All, trades, levels)}
          onAction={handleGetTimesheets}
        />
      </View>
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const mapStateToProps = (state: IRootState) => ({
  timesheetDate: state.timesheet,
  user: state.auth.authUser,
  trades: state.content.trades,
  levels: state.content.levels,
});

const mapDispatchToProps = {
  onGetTimesheets: getTimesheetsAsync.request,
  onExportCSVTimesheets: exportCSVTimesheetsAsync.request,
  onSetSelectedTimeSheet: setSelectedTimesheet,
  onSetActiveTab: setActiveTimesheetTab,
  onSetDateRange: setDateRange,
};

export default connect(mapStateToProps, mapDispatchToProps)(Timesheet);
